import { useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SandboxBanner — always-visible on/off toggle for SANDBOX_MODE.
 *
 * When SANDBOX_MODE=true:
 *   - Full orange banner across the top ("Sandbox Active · emails rerouting")
 *   - "Turn Sandbox OFF" button flips the backend env + hot-updates os.environ
 *
 * When SANDBOX_MODE=false (production email routing active):
 *   - Compact green pill in the top-right corner ("Live · Sandbox OFF")
 *   - "Turn Sandbox ON" button (single click, no confirmation dialog per
 *     Shannon's directive — this is Shannon's own switch)
 *
 * Non-admin users see nothing (endpoints 403).
 */
export const SandboxBanner = () => {
  const [status, setStatus] = useState(null); // null = still loading
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/admin/system/sandbox-status`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStatus(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 180000);
    window.addEventListener("mf-sandbox-changed", load);
    return () => { clearInterval(id); window.removeEventListener("mf-sandbox-changed", load); };
  }, [load]);

  const toggle = async (next) => {
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/system/sandbox-mode`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Toggle failed (${r.status})`);
      }
      const d = await r.json();
      setStatus({ sandbox_mode: d.sandbox_mode, redirect_email: d.redirect_email });
      window.dispatchEvent(new Event("mf-sandbox-changed"));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null; // still loading or not admin

  // ─── OFF STATE — compact live pill ────────────────────────────────
  if (!status.sandbox_mode) {
    return (
      <div data-testid="sandbox-live-pill" style={{
        position: "fixed", top: 12, right: 12, zIndex: 2000,
        display: "flex", alignItems: "center", gap: 10,
        background: "rgba(6, 78, 59, 0.92)",
        border: "1px solid #10b981",
        color: "#D1FAE5",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
        boxShadow: "0 4px 14px rgba(16, 185, 129, 0.25)",
      }}>
        <span style={{ fontSize: 12 }}>🟢</span>
        <span>LIVE · SANDBOX OFF</span>
        <button
          data-testid="sandbox-turn-on-btn"
          onClick={() => toggle(true)}
          disabled={busy}
          style={{
            background: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.28)",
            color: "#D1FAE5",
            padding: "3px 9px",
            borderRadius: 999,
            fontSize: 10, fontWeight: 800, letterSpacing: "0.06em",
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.5 : 1,
          }}
        >
          {busy ? "…" : "Turn ON"}
        </button>
        {error && <span style={{ color: "#FCA5A5", fontSize: 10 }} data-testid="sandbox-toggle-err">{error}</span>}
      </div>
    );
  }

  // ─── ON STATE — big orange banner (dismissable, but toggle persists) ──
  if (dismissed) {
    return (
      <div data-testid="sandbox-collapsed-pill" style={{
        position: "fixed", top: 12, right: 12, zIndex: 2000,
        display: "flex", alignItems: "center", gap: 10,
        background: "rgba(180, 83, 9, 0.94)",
        border: "1px solid #F59E0B",
        color: "#FFEDD5",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
      }}>
        <span>🟡</span>
        <span>SANDBOX ON</span>
        <button
          data-testid="sandbox-turn-off-btn"
          onClick={() => toggle(false)}
          disabled={busy}
          style={{
            background: "rgba(255,255,255,0.16)",
            border: "1px solid rgba(255,255,255,0.32)",
            color: "#FFEDD5",
            padding: "3px 9px",
            borderRadius: 999,
            fontSize: 10, fontWeight: 800, letterSpacing: "0.06em",
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.5 : 1,
          }}
        >
          {busy ? "…" : "Turn OFF"}
        </button>
        <button
          onClick={() => setDismissed(false)}
          title="Expand banner"
          style={{ background: "transparent", border: 0, color: "#FFEDD5", cursor: "pointer", fontSize: 12 }}
        >⤢</button>
      </div>
    );
  }

  return (
    <div data-testid="sandbox-banner" style={{
      background: "linear-gradient(135deg, #FFB347 0%, #FF8C42 100%)",
      color: "#030B1A",
      padding: "8px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      fontSize: 13,
      fontWeight: 800,
      letterSpacing: "0.02em",
      borderBottom: "2px solid #FF8C42",
      flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 16 }}>🟡</span>
      <span>SANDBOX MODE ACTIVE</span>
      <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.85 }}>
        — All outbound emails are rerouting to <strong>{status.redirect_email}</strong>. Real prospects/clients won't receive anything until sandbox is turned off.
      </span>
      <button
        data-testid="sandbox-turn-off-btn"
        onClick={() => toggle(false)}
        disabled={busy}
        style={{
          background: "#030B1A", color: "#FFB347",
          border: "1px solid #030B1A", padding: "4px 12px",
          borderRadius: 4, fontSize: 11, fontWeight: 800,
          letterSpacing: "0.04em", cursor: busy ? "wait" : "pointer",
          opacity: busy ? 0.5 : 1,
        }}
      >
        {busy ? "Flipping…" : "Turn OFF (go LIVE)"}
      </button>
      <button
        onClick={() => setDismissed(true)}
        data-testid="sandbox-banner-dismiss"
        title="Collapse to corner pill (toggle still available)"
        style={{ background: "rgba(3,11,26,0.15)", border: "none", color: "#030B1A", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 800, cursor: "pointer" }}
      >
        Hide
      </button>
      {error && <span style={{ color: "#7F1D1D", fontSize: 11 }} data-testid="sandbox-toggle-err">{error}</span>}
    </div>
  );
};

export default SandboxBanner;
